export const COUNTRY_FILTER_MODES = {
    ANY: "any",
    REGION: "region",
    CUSTOM: "custom",
};

export const COUNTRY_FILTER_MODE_OPTIONS = [
    { value: COUNTRY_FILTER_MODES.ANY, label: "Any country" },
    { value: COUNTRY_FILTER_MODES.REGION, label: "Same region" },
    { value: COUNTRY_FILTER_MODES.CUSTOM, label: "Custom" },
];

export const CONTINENT_GROUPS = [
    {
        id: "europe",
        label: "Europe",
        codes: [
            "AL", "AD", "AM", "AT", "BY", "BE", "BA", "BG", "HR", "CY",
            "CZ", "DK", "EE", "FI", "FR", "GE", "DE", "GR", "HU", "IS",
            "IE", "IT", "LV", "LI", "LT", "LU", "MT", "MD", "MC", "ME",
            "NL", "MK", "NO", "PL", "PT", "RO", "RU", "SM", "RS", "SK",
            "SI", "ES", "SE", "CH", "TR", "UA", "GB",
        ],
    },
    {
        id: "americas",
        label: "Americas",
        codes: ["US", "CA", "MX", "BR", "AR", "CL"],
    },
    {
        id: "asia-pacific",
        label: "Asia-Pacific",
        codes: ["IN", "JP", "KR", "SG"],
    },
    {
        id: "middle-east-africa",
        label: "Middle East & Africa",
        codes: ["DZ", "EG", "IQ", "LY", "MA", "TN", "IL", "AE", "SA"],
    },
    {
        id: "oceania",
        label: "Oceania",
        codes: ["AU"],
    },
];

export const GEO_QUEUE_COUNTRY_CODES = {
    eume: [
        "AL", "AD", "AM", "AT", "BY", "BE", "BA", "BG", "HR", "CY",
        "CZ", "DK", "EE", "FI", "FR", "GE", "DE", "GR", "HU", "IS",
        "DZ", "EG", "IE", "IQ", "IT", "LV", "LI", "LT", "LU", "LY", "MT",
        "MA", "MD", "MC", "ME", "NL", "MK", "NO", "PL", "PT", "RO",
        "SM", "RS", "SK", "SI", "TN", "ES", "SE", "CH", "TR", "UA",
        "RU", "GB", "IL", "AE", "SA",
    ],
    na: ["US", "CA", "MX"],
    sa: ["BR", "AR", "CL"],
    apac: ["JP", "KR", "SG", "IN", "AU"],
};

function normalizeCode(value) {
    return String(value || "").trim().toUpperCase();
}

const KNOWN_COUNTRY_CODES = [
    ...new Set(CONTINENT_GROUPS.flatMap((group) => group.codes.map(normalizeCode))),
];

function availableCountryCodes(countries = []) {
    if (countries.length > 0) {
        return countries
            .map((country) => normalizeCode(country.code2))
            .filter(Boolean);
    }
    return KNOWN_COUNTRY_CODES;
}

export function sameCodeSet(left = [], right = []) {
    if (left.length !== right.length) {
        return false;
    }
    const rightSet = new Set(right.map(normalizeCode));
    return left.every((code) => rightSet.has(normalizeCode(code)));
}

export function resolveCountryCode2(playerCountry, countries = []) {
    const raw = String(playerCountry || "").trim();
    if (!raw) {
        return null;
    }

    const upper = raw.toUpperCase();
    const aliased = upper === "UK" ? "GB" : upper;
    const fromList = countries.find((country) => {
        const code2 = normalizeCode(country.code2);
        const code3 = normalizeCode(country.code3);
        const name = String(country.displayName || "").toLowerCase();
        return (
            code2 === aliased ||
            code3 === aliased ||
            name === raw.toLowerCase()
        );
    });

    if (fromList?.code2) {
        return normalizeCode(fromList.code2);
    }

    return /^[A-Z]{2}$/.test(aliased) ? aliased : null;
}

function getFallbackFlagImageUrl(code2) {
    const code = String(code2 || "").trim().toLowerCase();
    if (!/^[a-z]{2}$/.test(code)) {
        return null;
    }
    return `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.3.2/flags/4x3/${code}.svg`;
}

export function resolvePlayerCountryPresentation(playerCountry, countries = []) {
    const code2 = resolveCountryCode2(playerCountry, countries);
    if (!code2) {
        return null;
    }

    const match = countries.find(
        (country) => String(country.code2 || "").toUpperCase() === code2,
    );

    return {
        code2,
        displayName: match?.displayName || code2,
        flagImageUrl: match?.flagImageUrl || getFallbackFlagImageUrl(code2),
    };
}

export function getSameRegionCountryCodes(playerCountry, countries = []) {
    const playerCode = resolveCountryCode2(playerCountry, countries);
    let queueId = "eume";
    if (playerCode) {
        const match = Object.entries(GEO_QUEUE_COUNTRY_CODES).find(([, codes]) =>
            codes.includes(playerCode),
        );
        if (match) {
            queueId = match[0];
        }
    }

    const allowed = new Set(GEO_QUEUE_COUNTRY_CODES[queueId] || []);
    return availableCountryCodes(countries).filter((code) => allowed.has(code));
}

export function isSameRegionSelection(list = [], countries = []) {
    if (!Array.isArray(list) || list.length === 0) {
        return false;
    }

    const listCodes = [
        ...new Set(list.map(normalizeCode).filter(Boolean)),
    ];
    const available = availableCountryCodes(countries);

    return Object.values(GEO_QUEUE_COUNTRY_CODES).some((queueCodes) => {
        const queue = new Set(queueCodes.map(normalizeCode));
        if (sameCodeSet(listCodes, queueCodes)) {
            return true;
        }

        const regionFromCatalog = available.filter((code) => queue.has(code));
        if (
            regionFromCatalog.length > 0 &&
            sameCodeSet(listCodes, regionFromCatalog)
        ) {
            return true;
        }

        if (!listCodes.every((code) => queue.has(code))) {
            return false;
        }

        const knownInQueue = KNOWN_COUNTRY_CODES.filter((code) =>
            queue.has(code),
        );
        return (
            knownInQueue.length > 0 &&
            knownInQueue.every((code) => listCodes.includes(code))
        );
    });
}

export function groupCountriesByContinent(countries = []) {
    const byCode = new Map(
        countries
            .filter((country) => country?.code2)
            .map((country) => [normalizeCode(country.code2), country]),
    );

    const groups = CONTINENT_GROUPS.map((group) => ({
        id: group.id,
        label: group.label,
        countries: group.codes
            .map((code) => byCode.get(code))
            .filter(Boolean)
            .sort((a, b) =>
                String(a.displayName).localeCompare(String(b.displayName)),
            ),
    })).filter((group) => group.countries.length > 0);

    const assigned = new Set(
        groups.flatMap((group) =>
            group.countries.map((country) => normalizeCode(country.code2)),
        ),
    );
    const other = countries
        .filter((country) => !assigned.has(normalizeCode(country.code2)))
        .sort((a, b) =>
            String(a.displayName).localeCompare(String(b.displayName)),
        );

    if (other.length > 0) {
        groups.push({
            id: "other",
            label: "Other",
            countries: other,
        });
    }

    return groups;
}

export function resolveCountryFilterMode(options, regionCodes = []) {
    const enabled = Boolean(options?.play_with_countries);
    const list = Array.isArray(options?.play_with_countries_list)
        ? options.play_with_countries_list
        : [];

    if (!enabled || list.length === 0) {
        return COUNTRY_FILTER_MODES.ANY;
    }
    if (regionCodes.length > 0 && sameCodeSet(list, regionCodes)) {
        return COUNTRY_FILTER_MODES.REGION;
    }
    if (isSameRegionSelection(list)) {
        return COUNTRY_FILTER_MODES.REGION;
    }
    return COUNTRY_FILTER_MODES.CUSTOM;
}
